/**
 * Zwoelf Monate des gewaehlten Jahres, drei Balken je Monat, kumulierte Linie.
 *
 * Handgezeichnetes SVG wie bisher — kein Chart-Framework. Der gewaehlte
 * Zeitraum wird hervorgehoben, die uebrigen Monate bleiben sichtbar (gedaempft):
 * der Vergleich mit dem Rest des Jahres ist der halbe Wert der Grafik.
 */
import React, { useMemo, useState } from 'react';
import type { CockpitModel } from '../../services/cockpitTypes';
import { MO, fmtCents, fmtCentsShort } from './cockpitUi';

const W = 900;
const H = 300;
const PAD = { t: 26, r: 24, b: 38, l: 62 };
const CW = W - PAD.l - PAD.r;
const CH = H - PAD.t - PAD.b;

const FORECAST = '#93c5fd';
const PERFORMED = '#10b981';
const BILLED = '#8b5cf6';
/** Rand (und Beschriftung) eines negativen Monats — Storno. */
const NEGATIVE = '#dc2626';
/** Die Nulllinie: sie trennt Leistung von Storno. */
const BASELINE = '#94a3b8';

export interface RevenueChartProps {
    model: CockpitModel;
    hideAmounts: boolean;
    onSelectMonth: (monthIndex: number) => void;
    todayMonth: number | null;
}

export const RevenueChart: React.FC<RevenueChartProps> = ({ model, hideAmounts, onSelectMonth, todayMonth }) => {
    // Der Hover merkt sich, ZU WELCHEM Modell er gehoert. Kommt ein neues
    // Modell (anderer Zeitraum, anderer Filter), gilt er nicht mehr — sonst
    // stuende der Tooltip mit den Zahlen von vorhin im Bild.
    const [hoverState, setHoverState] = useState<{ source: CockpitModel; index: number } | null>(null);
    const hover = hoverState && hoverState.source === model ? hoverState.index : null;
    const setHover = (index: number | null) =>
        setHoverState(index === null ? null : { source: model, index });

    const inPeriod = useMemo(() => new Set(model.periodMonths), [model.periodMonths]);

    const { min, max, ticks, cumulative, cumulativeMax } = useMemo(() => {
        const peak = model.months.reduce(
            (acc, m) => Math.max(acc, m.forecastCents, m.performedCents, m.billedCents), 0,
        );
        // Der TIEFSTE Monatswert zaehlt genauso wie der hoechste. Vorher
        // klemmte die Skala bei null (`Math.max(0, cents)`): ein Storno-Monat
        // — negatives ABGERECHNET, etwa −4.800 € im September — stand als
        // Nullbalken da, obwohl Kachel, Tooltip und Drilldown ihn zeigten.
        const trough = model.months.reduce(
            (acc, m) => Math.min(acc, m.forecastCents, m.performedCents, m.billedCents), 0,
        );
        const top = Math.max(peak, 100000) * 1.15;
        const step = top / 4;
        // Auf den Rasterschritt ABGERUNDET: so bekommt der negative Bereich
        // eine eigene beschriftete Achsenlinie und endet nicht irgendwo
        // zwischen zwei Ticks.
        const firstTick = trough < 0 ? -Math.ceil(-trough / step) : 0;
        const tickValues: number[] = [];
        for (let i = firstTick; i <= 4; i += 1) tickValues.push(i * step);
        const cum: number[] = [];
        for (const m of model.months) cum.push((cum[cum.length - 1] ?? 0) + m.performedCents);
        return {
            min: firstTick * step,
            max: top,
            ticks: tickValues,
            cumulative: cum,
            cumulativeMax: Math.max(cum[cum.length - 1] ?? 0, 1) * 1.1,
        };
    }, [model.months]);

    const colW = CW / 12;
    const barW = colW * 0.2;
    const span = max - min;
    const y = (cents: number) =>
        PAD.t + CH - ((Math.min(max, Math.max(min, cents)) - min) / span) * CH;
    // Die Kante, an der ALLE Balken haengen — ohne negativen Monat ist das
    // wie bisher der untere Rand.
    const zeroY = y(0);

    return (
        <svg
            viewBox={`0 0 ${W} ${H}`}
            className="w-full h-auto select-none overflow-visible"
            onMouseLeave={() => setHover(null)}
        >
            {ticks.map((value) => {
                const gy = y(value);
                return (
                    <g key={value}>
                        {/* Die Null bekommt keine Rasterlinie — sie bekommt die
                            Grundlinie weiter unten, deutlich kraeftiger. */}
                        {value !== 0 && <line x1={PAD.l} y1={gy} x2={W - PAD.r} y2={gy} stroke="#f1f5f9" />}
                        <text x={PAD.l - 8} y={gy + 4} textAnchor="end" className="text-[11px] fill-slate-400">
                            {fmtCentsShort(value, hideAmounts)}
                        </text>
                    </g>
                );
            })}

            <polyline
                fill="none"
                stroke={PERFORMED}
                strokeWidth="1.5"
                strokeDasharray="4 3"
                opacity="0.45"
                points={cumulative
                    // Eigene Skala, aber auf den Bereich [zeroY, Chart-Oben] statt
                    // auf die volle Chart-Hoehe (= [min, max]) abgebildet: sonst
                    // haengt die Linie bei einem negativen Monat unter die
                    // Nulllinie, obwohl der kumulierte Wert positiv ist.
                    .map((c, i) => `${PAD.l + i * colW + colW / 2},${zeroY - (Math.max(0, c) / cumulativeMax) * (zeroY - PAD.t)}`)
                    .join(' ')}
            />

            {model.months.map((m, i) => {
                const x = PAD.l + i * colW;
                const dim = !inPeriod.has(i);
                const opacity = dim ? 0.3 : 1;
                const fx = x + colW * 0.08;
                const px = fx + barW + 2;
                const bx = px + barW + 2;
                const bars: [number, number, string][] = [
                    [fx, m.forecastCents, FORECAST],
                    [px, m.performedCents, PERFORMED],
                    [bx, m.billedCents, BILLED],
                ];
                return (
                    <g key={i}>
                        {hover === i && <rect x={x} y={PAD.t} width={colW} height={CH} fill="#f8fafc" rx="3" />}
                        {bars.map(([barX, value, color]) => {
                            const vy = y(value);
                            const negative = value < 0;
                            return (
                                <rect
                                    key={color}
                                    x={barX}
                                    // Negativ: der Balken haengt UNTER der Nulllinie
                                    // und bekommt einen roten Rand — dieselbe Farbe
                                    // der Reihe, aber unverwechselbar ein Storno.
                                    y={negative ? zeroY : vy}
                                    width={barW}
                                    height={Math.max(0.5, Math.abs(vy - zeroY))}
                                    fill={color}
                                    opacity={opacity}
                                    rx="2"
                                    stroke={negative ? NEGATIVE : undefined}
                                    strokeWidth={negative ? 1 : undefined}
                                />
                            );
                        })}
                        {i === todayMonth && (
                            <line x1={x + colW / 2} y1={PAD.t + CH + 2} x2={x + colW / 2} y2={PAD.t + CH + 6} stroke="#3b82f6" strokeWidth="3" strokeLinecap="round" />
                        )}
                        <text
                            x={x + colW / 2}
                            y={H - PAD.b + 16}
                            textAnchor="middle"
                            className={`text-[11px] font-semibold ${dim ? 'fill-slate-300' : hover === i ? 'fill-blue-600' : 'fill-slate-500'}`}
                        >
                            {MO[i]}
                        </text>
                        <rect
                            x={x}
                            y={PAD.t}
                            width={colW}
                            height={CH + 18}
                            fill="transparent"
                            className="cursor-pointer"
                            onMouseEnter={() => setHover(i)}
                            onClick={() => onSelectMonth(i)}
                        />
                    </g>
                );
            })}

            <line
                x1={PAD.l}
                y1={zeroY}
                x2={W - PAD.r}
                y2={zeroY}
                stroke={BASELINE}
                strokeWidth="1"
                className="pointer-events-none"
            />

            {hover !== null && (() => {
                const cell = model.months[hover];
                const boxW = 148;
                const boxH = 68;
                const cx = PAD.l + hover * colW + colW / 2;
                const bxx = Math.min(W - PAD.r - boxW, Math.max(PAD.l, cx - boxW / 2));
                const rows: [string, number, string][] = [
                    ['Forecast', cell.forecastCents, FORECAST],
                    ['Erbracht', cell.performedCents, PERFORMED],
                    ['Abgerechnet', cell.billedCents, BILLED],
                ];
                return (
                    <g className="pointer-events-none">
                        <rect x={bxx} y={PAD.t + 2} width={boxW} height={boxH} rx="6" fill="#0f172a" opacity="0.94" />
                        {rows.map(([label, value, color], r) => (
                            <g key={label}>
                                <rect x={bxx + 8} y={PAD.t + 12 + r * 18} width={7} height={7} rx="2" fill={color} />
                                <text x={bxx + 20} y={PAD.t + 19 + r * 18} className="text-[11px] fill-slate-300">{label}</text>
                                <text x={bxx + boxW - 8} y={PAD.t + 19 + r * 18} textAnchor="end" className="text-[11px] font-semibold fill-white">
                                    {fmtCents(value, hideAmounts)}
                                </text>
                            </g>
                        ))}
                    </g>
                );
            })()}
        </svg>
    );
};

export const ChartLegend: React.FC = () => (
    <span className="text-[11px] text-slate-400 flex items-center gap-3">
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: FORECAST }} />Forecast</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: PERFORMED }} />Erbracht</span>
        <span className="flex items-center gap-1"><span className="inline-block w-2 h-2 rounded-sm" style={{ background: BILLED }} />Abgerechnet</span>
    </span>
);
